import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../redux/TranslationHelper';
import './FixedBgTextComponent.css';

const FixedBgTextComponent = ({ 
  imageUrl, 
  title, 
  description,
  componentProps = {},
  content = {},
  style = {},
  debug = false,
  viewMode,
  isPreview
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(true);
  const [hasError, setHasError] = useState(false);
  const componentRef = useRef(null);
  
  // 이미지 URL 처리
  // componentProps에서 bgImageUrl을 우선 사용하고, 없으면 imageUrl을 사용
  const finalImageUrl = componentProps?.bgImageUrl || imageUrl || content?.imageUrl || '';
  const finalTitle = title || content?.title || '';
  const finalDescription = description || content?.description || '';
  
  // HTML 콘텐츠 처리 - content가 문자열이면 HTML로 사용, 객체면 디바이스별 속성 또는 html 속성 확인
  const getHtmlContent = () => {
    let htmlContent = '';
    
    // content가 문자열인 경우 (단순 HTML)
    if (typeof content === 'string') {
      htmlContent = content;
    }
    
    // content가 객체인 경우 (디바이스별 속성 또는 HTML 속성 확인)
    if (content && typeof content === 'object') {
      // deviceProperty가 있는 경우 (디바이스별 설정)
      if (content.deviceProperty) {
        const isMobile = window.innerWidth <= 990;
        if (isMobile && content.deviceProperty.mobile) {
          // 모바일이면서 모바일 속성이 있으면 모바일 속성 사용
          htmlContent = content.deviceProperty.mobile;
        } else if (!isMobile && content.deviceProperty.pc) {
          // PC이면서 PC 속성이 있으면 PC 속성 사용
          htmlContent = content.deviceProperty.pc;
        } else if (content.deviceProperty.pc) {
          // 기본값으로 PC 속성 사용
          htmlContent = content.deviceProperty.pc;
        }
      }
      
      // html 속성이 있는 경우
      if (content.html) {
        htmlContent = content.html;
      }
    }
    
    // 기본값으로 빈 문자열 반환
    return htmlContent || '';
  };
  
  const htmlContent = getHtmlContent();
  
  // 이미지 로드 처리 - 직접 backgroundImage 적용
  useEffect(() => {
    if (!finalImageUrl) return;
    
    try {
      // 이미지 URL이 실제로 접근 가능한지 확인
      const img = new Image();
      
      img.onload = () => {
        setIsLoaded(true);
        setHasError(false);
        
        // 이미지가 성공적으로 로드되면 직접 배경 이미지를 설정
        if (componentRef.current) {
          componentRef.current.style.backgroundImage = `url(${finalImageUrl})`;
        }
      };
      
      img.onerror = () => {
        console.error(t('이미지 로드 실패:'), finalImageUrl);
        setIsLoaded(true);
        setHasError(true);
      };
      
      // 이미지 로드 시작
      img.src = finalImageUrl;
      
      // 이미 캐시된 이미지인 경우
      if (img.complete) {
        setIsLoaded(true);
        setHasError(false);
        
        // 이미지가 이미 캐시되어 있으면 직접 배경 이미지를 설정
        if (componentRef.current) {
          componentRef.current.style.backgroundImage = `url(${finalImageUrl})`;
        }
      }
    } catch (error) {
      console.error(t('이미지 로드 중 오류 발생:'), error);
      setIsLoaded(true);
      setHasError(true);
    }
  }, [finalImageUrl, t]);
  
  // 오버레이 투명도 설정
  const overlayOpacity = componentProps?.overlayOpacity || 0.5;
  const overlayStyle = {
    backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`
  };
  
  // 콘텐츠 정렬 설정
  const contentAlignment = componentProps?.contentAlignment || 'center';
  const contentStyle = {
    justifyContent: contentAlignment === 'top' ? 'flex-start' : 
                    contentAlignment === 'bottom' ? 'flex-end' : 'center'
  };
  
  // 텍스트 색상 설정
  const textColor = componentProps?.textColor || '#ffffff';
  const htmlContentStyle = {
    color: textColor
  };
  
  // 루트 컴포넌트 스타일 (CSS 변수 포함)
  const rootStyle = {
    ...style,
    // 배경 이미지는 useEffect에서 직접 설정
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    '--text-color': textColor,
    opacity: 1 // 항상 보이도록 설정
  };
  
  // 디버그용 데이터 속성
  const dataAttributes = {
    'data-loaded': isLoaded ? 'true' : 'false',
    'data-has-error': hasError ? 'true' : 'false',
    'data-debug': debug ? 'true' : 'false'
  };
  
  return (
    <div 
      className="fixed-bg-text-component loaded"
      ref={componentRef}
      style={rootStyle}
      {...dataAttributes}
    >
      <div className="overlay" style={overlayStyle}></div>
      <div className="content" style={contentStyle}>
        {htmlContent ? (
          <div className="html-content" style={htmlContentStyle} dangerouslySetInnerHTML={{ __html: htmlContent }} />
        ) : (
          <>
            <h2 className="title" style={{ color: textColor }}>
              {typeof finalTitle === 'string' ? t(finalTitle) : finalTitle}
            </h2>
            <p className="description" style={{ color: textColor }}>
              {typeof finalDescription === 'string' ? t(finalDescription) : finalDescription}
            </p>
          </>
        )}
      </div>
      
      {debug && (
        <div className="debug-info">
          <p>{t('Image URL')}: {finalImageUrl}</p>
          <p>{t('Is Loaded')}: {isLoaded ? t('Yes') : t('No')}</p>
          <p>{t('Has Error')}: {hasError ? t('Yes') : t('No')}</p>
          <p>{t('Window Width')}: {typeof window !== 'undefined' ? window.innerWidth : t('N/A')}</p>
        </div>
      )}
    </div>
  );
};

export default FixedBgTextComponent; 